import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PRAYER_TYPES } from '../../../config/constants';
import { PrayerType, ResolvedScheduleTime, ScheduleEntry, ScheduleOverride, Shul } from '../../../types';
import { getTodayString } from '../../../utils/dateShortcuts';
import { computeZmanimForDate } from '../../../utils/hebcalZmanim';
import { detectDayType } from '../../../utils/dayTypeDetector';
import DayLabels from '../../common/DayLabels';
import { addDays } from '../../../utils/jewishCalendar';
import { isGoldDay } from '../../../utils/minyanDay';
import { resolveShulSchedule } from '../../../utils/scheduleResolver';

const DAYS_SHOWN = 14;
const ONE_OFF_PRAYERS: PrayerType[] = ['shacharit', 'mincha', 'maariv', 'selichot', 'custom'];

const inputClass =
  'w-full px-3 py-2 rounded-[10px] border border-ink/20 bg-paper text-ink focus:outline-none focus:ring-2 focus:ring-ink/30 focus:border-ink text-sm';

type Sheet =
  | { kind: 'time'; date: string; entryId: string; prayer: PrayerType; shown: string; cancelled: boolean }
  | { kind: 'oneOff'; date: string };

function withOverride(entry: ScheduleEntry, date: string, change: ScheduleOverride | null): ScheduleEntry {
  const rest = (entry.overrides ?? []).filter((o) => o.date !== date);
  return { ...entry, overrides: change ? [...rest, change] : rest };
}

interface OneDateEditorProps {
  shul: Shul;
  onSave: (next: ScheduleEntry[]) => Promise<void>;
}

/**
 * Change one date: the coming days as the public will see them. Any time can be
 * changed or cancelled for that date only (ScheduleEntry.overrides), and a
 * one-off minyan added (an entry with specificDate).
 */
export default function OneDateEditor({ shul, onSave }: OneDateEditorProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'he' ? 'he' : 'en';
  const [from, setFrom] = useState(getTodayString());
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [time, setTime] = useState('');
  const [note, setNote] = useState('');
  const [noteHe, setNoteHe] = useState('');
  const [prayer, setPrayer] = useState<PrayerType>('mincha');
  const [saving, setSaving] = useState(false);
  const schedules = useMemo(() => shul.schedules ?? [], [shul.schedules]);

  const days = useMemo(() => {
    return Array.from({ length: DAYS_SHOWN }, (_, i) => {
      const date = addDays(from, i);
      const z = computeZmanimForDate(date);
      const rows = resolveShulSchedule(shul, date, detectDayType(z), z);
      const cancelled = schedules.flatMap((e) =>
        (e.overrides ?? []).filter((o) => o.date === date && o.cancelled).map(() => ({ entry: e }))
      );
      return { date, rows, cancelled };
    });
  }, [from, shul, schedules]);

  const dateLabel = (date: string) =>
    new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(`${date}T12:00:00`));

  const openTime = (date: string, row: { scheduleId: string; prayerType: PrayerType; displayTime: string }, cancelled = false) => {
    const entry = schedules.find((e) => e.id === row.scheduleId);
    const change = entry?.overrides?.find((o) => o.date === date);
    setTime(change?.timeRule.type === 'fixed' ? change.timeRule.time : /^\d\d:\d\d$/.test(row.displayTime) ? row.displayTime : '');
    setNote(change?.note ?? '');
    setNoteHe(change?.noteHe ?? '');
    setSheet({ kind: 'time', date, entryId: row.scheduleId, prayer: row.prayerType, shown: row.displayTime, cancelled });
  };

  const openOneOff = (date: string) => {
    setTime('');
    setNote('');
    setNoteHe('');
    setPrayer('mincha');
    setSheet({ kind: 'oneOff', date });
  };

  const commit = async (next: ScheduleEntry[]) => {
    setSaving(true);
    try {
      await onSave(next);
      setSheet(null);
    } finally {
      setSaving(false);
    }
  };

  const notes = () => ({ ...(note.trim() ? { note: note.trim() } : {}), ...(noteHe.trim() ? { noteHe: noteHe.trim() } : {}) });

  const updateEntry = (entryId: string, fn: (e: ScheduleEntry) => ScheduleEntry | null) =>
    schedules.flatMap((e) => {
      if (e.id !== entryId) return [e];
      const next = fn(e);
      return next ? [next] : [];
    });

  const sheetEntry = sheet?.kind === 'time' ? schedules.find((e) => e.id === sheet.entryId) : undefined;
  const hasChange = !!(sheet?.kind === 'time' && sheetEntry?.overrides?.some((o) => o.date === sheet.date));
  const isOneOffEntry = !!sheetEntry?.specificDate;

  const chip = 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold tabular-nums';

  return (
    <div className="space-y-3">
      <div className="rounded-[16px] border border-ink/10 bg-paper p-4 space-y-2">
        <h3 className="text-sm font-semibold text-ink">{t('setup.oneDate.title')}</h3>
        <p className="text-sm text-ash">{t('setup.oneDate.intro')}</p>
        <label htmlFor="one-date-from" className="block text-sm font-medium text-ink">
          {t('setup.oneDate.showFrom')}
        </label>
        <input
          id="one-date-from"
          type="date"
          value={from}
          onChange={(e) => e.target.value && setFrom(e.target.value)}
          className={inputClass}
        />
      </div>

      {days.map(({ date, rows, cancelled }) => {
        const gold = isGoldDay(date);
        const byPrayer = PRAYER_TYPES.map((p) => ({
          p,
          rows: rows.filter((r) => r.prayerType === p),
          cancelled: cancelled.filter((c) => c.entry.prayerType === p),
        })).filter((g) => g.rows.length || g.cancelled.length);
        return (
          <div key={date} className={`rounded-[16px] border p-3 space-y-2 ${gold ? 'border-gold-600/40 bg-gold-50' : 'border-ink/10 bg-paper'}`}>
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <span className="text-sm font-semibold text-ink">{dateLabel(date)}</span>
              <DayLabels date={date} />
            </div>
            {byPrayer.length === 0 && <p className="text-xs text-ash">{t('setup.oneDate.noTimes')}</p>}
            {byPrayer.map((g) => (
              <div key={g.p} className="grid grid-cols-[6rem_minmax(0,1fr)] gap-2 items-start text-sm">
                <span className="text-ink pt-1">{t(`prayers.${g.p}`)}</span>
                <span className="flex flex-wrap gap-1">
                  {g.rows.map((r: ResolvedScheduleTime) => {
                    const oneOff = !!schedules.find((e) => e.id === r.scheduleId)?.specificDate;
                    return (
                      <button
                        key={`${r.scheduleId}-${r.displayTime}`}
                        type="button"
                        onClick={() => openTime(date, r)}
                        className={`${chip} ${r.isOverride || oneOff ? 'bg-gold-50 text-gold-700 ring-1 ring-gold-600' : 'bg-weekday-50 text-weekday-700'}`}
                      >
                        {oneOff ? '+ ' : ''}
                        {r.displayTime}
                      </button>
                    );
                  })}
                  {g.cancelled.map(({ entry }) => (
                    <button
                      key={`x-${entry.id}`}
                      type="button"
                      onClick={() => openTime(date, { scheduleId: entry.id, prayerType: entry.prayerType, displayTime: t('setup.oneDate.cancelled') }, true)}
                      className={`${chip} bg-danger-50 text-danger-700 line-through`}
                    >
                      {t('setup.oneDate.cancelled')}
                    </button>
                  ))}
                </span>
              </div>
            ))}
            <button type="button" onClick={() => openOneOff(date)} className="text-xs font-medium text-weekday-700 hover:underline">
              {t('setup.oneDate.addOneOff')}
            </button>
          </div>
        );
      })}

      <button type="button" onClick={() => setFrom(addDays(from, DAYS_SHOWN))} className="text-sm font-medium text-weekday-700 hover:underline">
        {t('setup.oneDate.later')}
      </button>

      {sheet && (
        <div
          className="fixed inset-0 z-50 bg-ink/40 flex items-end sm:items-center justify-center"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSheet(null);
          }}
        >
          <div role="dialog" aria-modal="true" className="w-full sm:max-w-md bg-paper rounded-t-[20px] sm:rounded-[20px] p-4 space-y-3 max-h-[88vh] overflow-y-auto">
            {sheet.kind === 'time' ? (
              <>
                <h3 className="text-base font-semibold text-ink">
                  {t(`prayers.${sheet.prayer}`)} {sheet.cancelled ? '' : sheet.shown}
                </h3>
                <p className="text-sm text-ash">{t('setup.oneDate.forDateOnly', { date: dateLabel(sheet.date) })}</p>
                {!isOneOffEntry && (
                  <>
                    <label htmlFor="one-date-time" className="block text-sm font-medium text-ink">
                      {t('setup.oneDate.newTime')}
                    </label>
                    <input id="one-date-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputClass} />
                  </>
                )}
                <input id="one-date-note" aria-label={t('setup.ui.noteEn')} placeholder={t('setup.oneDate.noteForPublic')} value={note} onChange={(e) => setNote(e.target.value)} className={inputClass} />
                <input id="one-date-note-he" aria-label={t('setup.ui.noteHe')} placeholder={t('setup.ui.noteHe')} value={noteHe} onChange={(e) => setNoteHe(e.target.value)} className={inputClass} dir="rtl" />
                <div className="flex flex-col gap-2">
                  {!isOneOffEntry && (
                    <button
                      type="button"
                      disabled={saving || !time}
                      onClick={() =>
                        void commit(updateEntry(sheet.entryId, (e) => withOverride(e, sheet.date, { date: sheet.date, timeRule: { type: 'fixed', time }, ...notes() })))
                      }
                      className="px-4 py-2 rounded-[10px] bg-ink text-paper text-sm font-medium disabled:opacity-50"
                    >
                      {t('setup.oneDate.changeOnly')}
                    </button>
                  )}
                  {!isOneOffEntry && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() =>
                        void commit(updateEntry(sheet.entryId, (e) => withOverride(e, sheet.date, { date: sheet.date, timeRule: e.timeRule, cancelled: true, ...notes() })))
                      }
                      className="px-4 py-2 rounded-[10px] border border-ink/20 text-sm font-medium text-ink disabled:opacity-50"
                    >
                      {t('setup.oneDate.cancelDate')}
                    </button>
                  )}
                  {hasChange && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void commit(updateEntry(sheet.entryId, (e) => withOverride(e, sheet.date, null)))}
                      className="px-4 py-2 rounded-[10px] border border-ink/20 text-sm font-medium text-ink disabled:opacity-50"
                    >
                      {t('setup.oneDate.backToNormal')}
                    </button>
                  )}
                  {isOneOffEntry && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void commit(updateEntry(sheet.entryId, () => null))}
                      className="px-4 py-2 rounded-[10px] border border-danger-600 text-sm font-medium text-danger-700 disabled:opacity-50"
                    >
                      {t('setup.oneDate.removeOneOff')}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <h3 className="text-base font-semibold text-ink">{t('setup.oneDate.oneOffTitle', { date: dateLabel(sheet.date) })}</h3>
                <label htmlFor="one-off-prayer" className="block text-sm font-medium text-ink">
                  {t('setup.oneDate.prayer')}
                </label>
                <select id="one-off-prayer" value={prayer} onChange={(e) => setPrayer(e.target.value as PrayerType)} className={inputClass}>
                  {ONE_OFF_PRAYERS.map((p) => (
                    <option key={p} value={p}>
                      {p === 'custom' ? t('setup.oneDate.otherPrayer') : t(`prayers.${p}`)}
                    </option>
                  ))}
                </select>
                <label htmlFor="one-off-time" className="block text-sm font-medium text-ink">
                  {t('setup.oneDate.time')}
                </label>
                <input id="one-off-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputClass} />
                <input id="one-off-note" aria-label={t('setup.ui.noteEn')} placeholder={t('setup.oneDate.noteForPublic')} value={note} onChange={(e) => setNote(e.target.value)} className={inputClass} />
                <input id="one-off-note-he" aria-label={t('setup.ui.noteHe')} placeholder={t('setup.ui.noteHe')} value={noteHe} onChange={(e) => setNoteHe(e.target.value)} className={inputClass} dir="rtl" />
                <button
                  type="button"
                  disabled={saving || !time}
                  onClick={() =>
                    void commit([
                      ...schedules,
                      {
                        id: crypto.randomUUID(),
                        prayerType: prayer,
                        dayTypes: [],
                        timeRule: { type: 'fixed', time },
                        active: true,
                        specificDate: sheet.date,
                        overrides: [],
                        ...notes(),
                      },
                    ])
                  }
                  className="w-full px-4 py-2 rounded-[10px] bg-ink text-paper text-sm font-medium disabled:opacity-50"
                >
                  {t('setup.oneDate.add')}
                </button>
              </>
            )}
            <button type="button" onClick={() => setSheet(null)} className="w-full px-4 py-2 rounded-[10px] text-sm text-ash">
              {t('setup.ui.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
